import { useState } from 'react';
import type { Company } from '../types/company';
import { __ } from '@/hub-shared/utils/i18n';
import MissionStartSubscriptionModal from '@/hub-shared/components/MissionStartSubscriptionModal';
import {
  buildVSMEFormUrl,
  createVSMEReport,
  fetchMissionSummary,
  startMission,
  type MissionSummary,
} from '../utils/vsmeApi';
import { TableRowActions, viewIcon, deleteIcon, vsmeIconBlack, getBootstrapData } from '@tpct/admin-ui';
import type { TableRowAction } from '@tpct/admin-ui';
import { isHubApiError } from '@tpct/mission-core/utils/hubApiError';
import {
  companyCanStartMission,
  missionStartPermissionDeniedMessage,
} from '@tpct/mission-core/utils/missionStartPermissionGate';
import { vsmeMissionsListRelativePath, vsmeMissionSetupRelativePath } from '@tpct/mission-core/utils/vsmePortalRoutes';

type CompanyRowProps = {
  company: Company;
  onDelete: (companyId: number) => void;
  navigateTo?: (path: string) => void;
  bootstrapKey?: string;
};

const formatCreationDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

const hasCurrentMission = (company: Company): boolean =>
  (company.current_mission?.id ?? 0) > 0;

const hasContinuableMission = (company: Company): boolean =>
  company.current_mission?.can_continue === true && hasCurrentMission(company);

const missionActionLabel = (company: Company): string => {
  if (hasContinuableMission(company)) {
    return __('Continuer la mission');
  }
  if (hasCurrentMission(company)) {
    return __('Voir les rapports VS (vsme)');
  }
  return __('Démarrer mission');
};

const reportsListPath = vsmeMissionsListRelativePath();

const resolveReportIdFromCompany = (company: Company): number | null => {
  const currentReport = company.report_status?.current_report;
  if (currentReport !== null && currentReport !== undefined && currentReport > 0) {
    return currentReport;
  }

  return null;
};

const CompanyRow = ({
  company,
  onDelete,
  navigateTo,
  bootstrapKey = 'AccountingHubCompaniesAdmin',
}: CompanyRowProps) => {
  const [isStartingMission, setIsStartingMission] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const bootstrap = getBootstrapData(bootstrapKey) as {
    canStartDossierMission?: boolean;
    subscriptionTierUrl?: string;
  } | null;
  const canStartDossierMission = bootstrap?.canStartDossierMission !== false;
  const subscriptionTierUrl = bootstrap?.subscriptionTierUrl ?? '/pricing/';

  const handleDelete = () => {
    if (window.confirm(`${__("Êtes-vous sûr de vouloir supprimer l'entreprise")} "${company.name}" ?`)) {
      onDelete(company.id);
    }
  };

  const openMissionWizard = (missionId: number) => {
    const wizardPath = `${vsmeMissionSetupRelativePath(missionId)}?${new URLSearchParams({ company_id: String(company.id) }).toString()}`;
    if (navigateTo) {
      navigateTo(wizardPath);
    } else {
      window.location.href = `/hub${wizardPath}`;
    }
  };

  const openReportsList = () => {
    if (navigateTo) {
      navigateTo(reportsListPath);
      return;
    }
    window.location.href = `/hub${reportsListPath}`;
  };

  const goToVsmeForm = (reportId: number, missionId: number) => {
    window.location.assign(buildVSMEFormUrl(String(reportId), company.id, missionId));
  };

  const continueExistingMission = async (missionId: number) => {
    let reportId = resolveReportIdFromCompany(company);
    let summary: MissionSummary | null = null;

    if (reportId === null) {
      summary = await fetchMissionSummary(missionId);
      if (summary.linked_report_id !== null && summary.linked_report_id > 0) {
        reportId = summary.linked_report_id;
      }
    }

    if (reportId !== null) {
      goToVsmeForm(reportId, missionId);
      return;
    }

    if (summary === null) {
      summary = await fetchMissionSummary(missionId);
    }

    const report = await createVSMEReport(
      company.id,
      summary.mission.period_start,
      summary.mission.period_end,
      missionId,
    );

    goToVsmeForm(Number(report.id), missionId);
  };

  const handleStartVSME = async () => {
    if (isStartingMission) {
      return;
    }

    const requiresMissionStartPermission = !hasCurrentMission(company) || hasContinuableMission(company);
    if (requiresMissionStartPermission && !companyCanStartMission(company)) {
      return;
    }

    if (!canStartDossierMission && !hasCurrentMission(company)) {
      setSubscriptionModalOpen(true);
      return;
    }

    if (hasCurrentMission(company) && !hasContinuableMission(company)) {
      openReportsList();
      return;
    }

    setIsStartingMission(true);

    try {
      if (hasContinuableMission(company)) {
        const missionId = company.current_mission?.id;
        if (missionId === undefined || missionId <= 0) {
          throw new Error(__('Mission introuvable pour ce dossier.'));
        }

        await continueExistingMission(missionId);
        return;
      }

      if (!company.fiscal_year_start || !company.fiscal_year_end) {
        alert(__("Cette entreprise n'a pas de dates d'exercice definies. Veuillez les configurer d'abord."));
        return;
      }

      const payload = await startMission(company.id);
      openMissionWizard(payload.mission.id);
    } catch (error) {
      console.error('Error starting mission:', error);
      if (isHubApiError(error) && error.code === 'trial.dossier_mission_denied') {
        setSubscriptionModalOpen(true);
        return;
      }
      alert(
        error instanceof Error
          ? `${__('Erreur lors du démarrage de la mission VS:')} ${error.message}`
          : __('Une erreur est survenue lors du démarrage de la mission VS.'),
      );
    } finally {
      setIsStartingMission(false);
    }
  };

  const requiresMissionStartPermission = !hasCurrentMission(company) || hasContinuableMission(company);
  const canStartMissionForCompany = companyCanStartMission(company);
  // The action is disabled on denial, so the row itself has to carry the reason —
  // otherwise the click is silently swallowed and reads as a broken button.
  const missionStartDenied = requiresMissionStartPermission && !canStartMissionForCompany;
  const missionActionDisabled = isStartingMission || missionStartDenied;

  const actions: TableRowAction[] = [
    ...(navigateTo ? [{
      key: 'view',
      label: __('Voir'),
      icon: viewIcon,
      onClick: () => navigateTo(`/companies/company/${company.id}`),
    }] : []),
    {
      key: 'vsme',
      label: missionActionLabel(company),
      icon: vsmeIconBlack,
      onClick: handleStartVSME,
      loading: isStartingMission,
      disabled: missionActionDisabled,
    },
    ...(company.capabilities?.delete === true ? [{
      key: 'delete',
      label: __('Supprimer'),
      icon: deleteIcon,
      onClick: handleDelete,
      variant: 'delete' as const,
    }] : []),
  ];

  return (
    <>
      <tr className="companies-table__row">
        <td className="companies-table__name">
          <p>{company.name}</p>
        </td>
        <td className="companies-table__siret">
          {company.siren}
        </td>
        <td className="companies-table__users">
          {company.user_count ?? 0}
        </td>
        <td className="companies-table__created">
          {formatCreationDate(company.created)}
        </td>
        <td className="companies-table__actions">
          <TableRowActions
            actions={actions}
            error={missionStartDenied ? missionStartPermissionDeniedMessage() : null}
            overflowLabel={__('Plus d’actions')}
          />
        </td>
      </tr>
      {subscriptionModalOpen ? (
        <MissionStartSubscriptionModal
          isOpen={subscriptionModalOpen}
          onClose={() => setSubscriptionModalOpen(false)}
          subscriptionTierUrl={subscriptionTierUrl}
        />
      ) : null}
    </>
  );
};

export default CompanyRow;
